// One row per week number. A programme can hold the same week number twice: programme 193 has two
// rows numbered 7, made a fraction of a second apart on 14 September 2026. The client screen counts weeks
// by position (weeks.length), so with a duplicate it took 8 rows for 8 weeks when there were only 7: on
// finishing week 7 it moved the client to week 8, tried to copy a "week 8" that did not exist, and moved
// them on to week 9, which did not exist either, leaving them stuck on week 7 with no way on (reported by
// the client on 25 October 2026). The later row of a pair (higher id) is kept: it is the copy that was
// trained in. Nothing in the database is changed or removed.
export const oneRowPerWeek = (rows) => {
  const byNumber = new Map();
  for (const week of rows || []) {
    const kept = byNumber.get(week.week_number);
    if (!kept || week.id > kept.id) byNumber.set(week.week_number, week);
  }
  return [...byNumber.values()].sort((a, b) => a.week_number - b.week_number);
};

// The client's calendar weeks. Week N runs from clock + (N - 1) weeks to clock + N weeks, where the clock
// is program_assignments.week_clock_from, or assigned_at when no coach has set the week (migration 092).
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

// The clock that makes `week` the current calendar week from `now`: what the coach's Set Week saves, so
// the client's screen does not move them on again and record misses for days they never had (programme
// 193, 25 October 2026).
export const weekClockFor = (week, now = new Date()) => new Date(now.getTime() - (week - 1) * WEEK_MS).toISOString();

// The calendar week the clock says it is (autoAdvanceMissedWeeks moves the client on to it).
export const calendarWeek = (clock, now = new Date()) =>
  Math.floor(Math.floor((now - new Date(clock)) / DAY_MS) / 7) + 1;

// When the clock's week `week` ends.
export const weekEndsAt = (clock, week) => new Date(new Date(clock).getTime() + week * WEEK_MS);

// The reminder before a week closes (owner, 25 October 2026: a missed day stays a true record, so warn
// before, rather than reopen after). Shown in the last two days of the client's calendar week while
// workouts are left; null otherwise, including when the client is ahead of the calendar.
export const weekEndingSoon = ({ clock, week, workoutsLeft, now = new Date() }) => {
  if (!clock || !week || !(workoutsLeft > 0)) return null;
  const endsAt = weekEndsAt(clock, week);
  const left = endsAt - now;
  if (left <= 0 || left > 2 * DAY_MS) return null;
  return { workoutsLeft, endsAt };
};

// "today at 07:33", "tomorrow at 07:33" or "Thursday at 07:33", in the phone's own time zone.
export const whenWeekEnds = (endsAt, now = new Date()) => {
  const time = endsAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const dayOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((dayOf(endsAt) - dayOf(now)) / DAY_MS);
  if (days === 0) return `today at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  return `${endsAt.toLocaleDateString('en-GB', { weekday: 'long' })} at ${time}`;
};
